import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  SegmentedControl,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import {
  IconAlertCircle,
  IconArrowsSort,
  IconBulb,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
  IconPlayerPlay,
  IconSearch,
  IconX,
} from '@tabler/icons-react'
import { useCan, useUserTenants, useAuthStore } from '@/store/auth'
import { finopsApi } from '@/api/finops'
import { formatUSD } from '@/utils/finops'
import { EmptyState } from '@/components/ui'
import type {
  FinopsRecommendation,
  FinopsRecommendationLevel,
  FinopsRecommendationStatus,
} from '@/api/finops'

type StatusFilter = 'all' | 'detected' | 'pending' | 'executing' | 'closed'
type SortCol = 'rec_type' | 'account_id' | 'estimated' | 'confidence' | 'risk' | 'status'
type SortDir = 'asc' | 'desc'

const STATUS_LABELS: Record<FinopsRecommendationStatus, string> = {
  detected: 'Detectada',
  recommended: 'Recomendada',
  approved: 'Aprovada',
  executing: 'Em execução',
  executed: 'Executada',
  verified: 'Verificada',
  failed: 'Falhou',
  cancelled: 'Cancelada',
  dismissed: 'Descartada',
}

const STATUS_COLORS: Record<FinopsRecommendationStatus, string> = {
  detected: 'gray',
  recommended: 'blue',
  approved: 'cyan',
  executing: 'yellow',
  executed: 'teal',
  verified: 'green',
  failed: 'red',
  cancelled: 'gray',
  dismissed: 'gray',
}

const LEVEL_LABELS: Record<FinopsRecommendationLevel, string> = {
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
}

const CONFIDENCE_COLORS: Record<FinopsRecommendationLevel, string> = {
  high: 'green',
  medium: 'yellow',
  low: 'red',
}

// Risco: alto risco = vermelho (atenção), baixo = verde (seguro).
const RISK_COLORS: Record<FinopsRecommendationLevel, string> = {
  high: 'red',
  medium: 'yellow',
  low: 'green',
}

const REC_TYPE_LABELS: Record<string, string> = {
  rightsizing: 'Rightsizing EC2',
  graviton_migration: 'Migração Graviton (ARM)',
  savings_plans: 'Savings Plan',
  ri: 'Reserved Instance',
  gp2_to_gp3: 'EBS gp2 → gp3',
  idle_eip: 'Elastic IP ocioso',
  idle_load_balancer: 'Load Balancer ocioso',
  idle_nat_gateway: 'NAT Gateway ocioso',
  rds_idle: 'RDS ocioso',
  s3_lifecycle: 'S3 sem lifecycle',
  orphan_ebs: 'EBS órfão',
  old_snapshot: 'Snapshot antigo',
  old_ami: 'AMI antiga',
}

function recTypeLabel(recType: string): string {
  return REC_TYPE_LABELS[recType] ?? recType
}

function statusFilterToApi(filter: StatusFilter): string | undefined {
  switch (filter) {
    case 'detected':
      return 'detected'
    case 'pending':
      return 'recommended,approved'
    case 'executing':
      return 'executing'
    case 'closed':
      return 'executed,verified,failed,cancelled,dismissed'
    default:
      return undefined
  }
}

function asNumber(v: number | string | null | undefined): number | null {
  if (v == null) return null
  const n = typeof v === 'string' ? Number(v) : v
  return Number.isFinite(n) ? n : null
}

// Order for level sorts (high > medium > low → numeric desc for "important first")
const LEVEL_ORDER: Record<FinopsRecommendationLevel, number> = {
  high: 3,
  medium: 2,
  low: 1,
}

const STATUS_ORDER: Record<FinopsRecommendationStatus, number> = {
  executing: 9,
  approved: 8,
  recommended: 7,
  detected: 6,
  failed: 5,
  executed: 4,
  verified: 3,
  cancelled: 2,
  dismissed: 1,
}

// Active statuses count toward group savings subtotal
const ACTIVE_STATUSES = new Set<FinopsRecommendationStatus>([
  'detected',
  'recommended',
  'approved',
  'executing',
])

function recExplanation(r: FinopsRecommendation): string {
  const p = (r.detection_payload ?? {}) as Record<string, unknown>
  const save = asNumber(r.estimated_monthly_savings_usd)
  const econ = save != null ? ` Economia estimada: ${formatUSD(save)}/mês.` : ''
  switch (r.rec_type) {
    case 'graviton_migration':
      return `Migrar a instância ${(p.current_type as string) ?? '?'} (x86) para ${(p.target_type as string) ?? 'equivalente Graviton'} (ARM, mais barata).${econ} Risco: requer imagem/binários compatíveis com ARM (recompilar e validar).`
    case 'rightsizing':
      return `Instância subutilizada — redimensionar para um tipo menor${p.target_type ? ` (${(p.current_type as string) ?? '?'} → ${p.target_type as string})` : ''}.${econ}`
    case 'orphan_ebs':
      return `Volume EBS não anexado a nenhuma instância${p.size_gb ? `: ${p.size_gb as string} GB ${(p.volume_type as string) ?? ''}` : ''}. Remover elimina a cobrança.${econ}`
    case 'gp2_to_gp3':
      return `Converter o volume gp2 → gp3 (mesmo desempenho base, ~20% mais barato)${p.size_gb ? `, ${p.size_gb as string} GB` : ''}.${econ}`
    case 'old_snapshot':
      return `Snapshot antigo sem uso${p.age_days ? ` (${p.age_days as string} dias)` : ''}. Remover reduz custo de armazenamento.${econ}`
    case 'old_ami':
      return `AMI antiga sem uso${p.age_days ? ` (${p.age_days as string} dias)` : ''} (e snapshots associados). Remover reduz custo de armazenamento.${econ}`
    case 'idle_eip':
      return `Elastic IP sem associação é cobrado por hora ociosa. Liberar o IP elimina a cobrança.${econ}`
    case 'idle_load_balancer':
      return `Load Balancer sem targets/saudável-zero — provavelmente ocioso. Avaliar remoção.${econ}`
    case 'idle_nat_gateway':
      return `NAT Gateway com tráfego desprezível — possível ociosidade. Avaliar consolidação/remoção.${econ}`
    case 'rds_idle':
      return `Instância RDS com baixíssima atividade (conexões/CPU). Avaliar parada/downsize.${econ}`
    case 's3_lifecycle':
      return `Bucket S3 sem política de lifecycle — objetos antigos poderiam ir para classes mais baratas/expirar.${econ}`
    case 'savings_plans':
    case 'ri':
      return `Compromisso de uso (Savings Plan/Reserved Instance) cobriria uso on-demand recorrente.${econ}`
    default:
      return save != null
        ? `Economia estimada de ${formatUSD(save)}/mês ao aplicar o ajuste.`
        : 'Recomendação de otimização de custo.'
  }
}

export interface RecommendationsTabProps {
  /**
   * Quando definido, a tabela vira uma visão filtrada por tipo(s) de
   * recomendação (Rightsizing, Savings Plans/RI). Sem `recTypes` mostra tudo
   * e exibe o filtro de tipo.
   */
  recTypes?: string[]
}

// Number of columns (used for colSpan in group header rows)
const COL_COUNT = 9

export function RecommendationsTab({ recTypes }: RecommendationsTabProps) {
  const can = useCan()
  const qc = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [recTypeFilter, setRecTypeFilter] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<{ col: SortCol; dir: SortDir }>({ col: 'estimated', dir: 'desc' })
  // Set of expanded group tenant_ids
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set())
  // Set of expanded row ids (detail panel)
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

  // Capacidades SP4: recommend/dismiss = finops:recommend; approve/execute =
  // finops:execute (substitui o legado recommend_cleanup/approve_cleanup).
  const canRecommend = can('finops', 'recommend')
  const canExecute = can('finops', 'execute')

  const userTenants = useUserTenants()
  const isAdmin = useAuthStore((s) => !!s.user?.is_admin)
  const writableTenantIds = useMemo(
    () => new Set(userTenants.map((t) => t.id)),
    [userTenants],
  )

  const isFilteredView = recTypes != null && recTypes.length > 0
  const effectiveRecType = isFilteredView
    ? recTypes.join(',')
    : recTypeFilter ?? undefined

  const recsQuery = useQuery({
    queryKey: ['finops-recommendations-all', effectiveRecType ?? 'all', statusFilter],
    queryFn: async () => {
      const all: FinopsRecommendation[] = []
      let cursor: string | undefined = undefined
      for (let i = 0; i < 20; i++) {           // 20 * 100 = 2000 max
        const page = await finopsApi.listRecommendationsPlatform({
          rec_type: effectiveRecType,
          status: statusFilterToApi(statusFilter),
          limit: 100,
          cursor,
        })
        all.push(...page.items)
        if (!page.has_more || !page.next_cursor) break
        cursor = page.next_cursor
      }
      return all
    },
    staleTime: 10_000,
  })

  const allItems = recsQuery.data ?? []

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['finops-recommendations-all'] })
    qc.invalidateQueries({ queryKey: ['finops-recommendations-savings'] })
  }

  const onMutationError = (e: unknown) =>
    notifications.show({ title: 'Erro', message: String(e), color: 'red' })

  const approveMut = useMutation({
    mutationFn: (r: FinopsRecommendation) =>
      finopsApi.approveRecommendation(r.tenant_id, r.id),
    onSuccess: () => {
      notifications.show({ title: 'Aprovada', message: 'Recomendação aprovada.', color: 'green' })
      invalidate()
    },
    onError: onMutationError,
  })

  const executeMut = useMutation({
    mutationFn: (r: FinopsRecommendation) =>
      // Backend exige Idempotency-Key em formato UUID (api.py: uuid.UUID(...));
      // chave não-UUID (`exec-${id}-${ts}`) dava 422 invalid_idempotency_key.
      finopsApi.executeRecommendation(r.tenant_id, r.id, crypto.randomUUID()),
    onSuccess: () => {
      notifications.show({
        title: 'Execução enfileirada',
        message: 'A ação será processada em segundos.',
        color: 'yellow',
      })
      invalidate()
    },
    onError: onMutationError,
  })

  const cancelMut = useMutation({
    mutationFn: (r: FinopsRecommendation) =>
      finopsApi.cancelRecommendation(r.tenant_id, r.id),
    onSuccess: () => {
      notifications.show({ title: 'Cancelada', message: 'Recomendação cancelada.', color: 'gray' })
      invalidate()
    },
    onError: onMutationError,
  })

  const dismissMut = useMutation({
    mutationFn: (r: FinopsRecommendation) =>
      finopsApi.dismissRecommendation(r.tenant_id, r.id),
    onSuccess: () => {
      notifications.show({ title: 'Descartada', message: 'Recomendação descartada.', color: 'gray' })
      invalidate()
    },
    onError: onMutationError,
  })

  // Tipos disponíveis para o seletor (só na visão geral, não filtrada).
  const recTypeOptions = useMemo(() => {
    const set = new Set<string>()
    allItems.forEach((r) => set.add(r.rec_type))
    return Array.from(set).map((t) => ({ value: t, label: recTypeLabel(t) }))
  }, [allItems])

  const canActOn = (r: FinopsRecommendation) => isAdmin || writableTenantIds.has(r.tenant_id)

  const openExecuteConfirm = (rec: FinopsRecommendation) => {
    modals.openConfirmModal({
      title: 'Confirmar execução',
      children: (
        <Stack gap="xs">
          <Text size="sm">
            Tipo: <strong>{recTypeLabel(rec.rec_type)}</strong>
          </Text>
          <Text size="sm">
            Recurso: <code>{rec.resource_id}</code>
          </Text>
          <Text size="sm">Conta: {rec.account_id}</Text>
          {asNumber(rec.estimated_monthly_savings_usd) != null && (
            <Text size="sm" c="green">
              Economia estimada: {formatUSD(asNumber(rec.estimated_monthly_savings_usd))}/mês
            </Text>
          )}
          <Text size="xs" c="red">
            Esta ação altera ou remove o recurso na AWS.
          </Text>
        </Stack>
      ),
      labels: { confirm: 'Executar', cancel: 'Cancelar' },
      confirmProps: { color: 'red' },
      onConfirm: () => executeMut.mutate(rec),
    })
  }

  const renderRowActions = (r: FinopsRecommendation) => {
    if (!canActOn(r)) return <Text size="xs" c="dimmed">—</Text>
    if (r.status === 'detected' || r.status === 'recommended') {
      return (
        <Group gap="xs" wrap="nowrap">
          {canExecute && (
            <Button
              size="xs"
              variant="light"
              color="cyan"
              leftSection={<IconCheck size={14} />}
              onClick={(e) => { e.stopPropagation(); approveMut.mutate(r) }}
              loading={approveMut.isPending && approveMut.variables?.id === r.id}
            >
              Aprovar
            </Button>
          )}
          {canRecommend && (
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              leftSection={<IconX size={14} />}
              onClick={(e) => { e.stopPropagation(); dismissMut.mutate(r) }}
              loading={dismissMut.isPending && dismissMut.variables?.id === r.id}
            >
              Descartar
            </Button>
          )}
        </Group>
      )
    }
    if (r.status === 'approved') {
      return (
        <Group gap="xs" wrap="nowrap">
          {r.executable ? (
            canExecute && (
              <Button
                size="xs"
                variant="light"
                color="red"
                leftSection={<IconPlayerPlay size={14} />}
                onClick={(e) => { e.stopPropagation(); openExecuteConfirm(r) }}
                loading={executeMut.isPending && executeMut.variables?.id === r.id}
              >
                Executar
              </Button>
            )
          ) : (
            <Badge color="teal" variant="light">Aceita — ação manual</Badge>
          )}
          <Button
            size="xs"
            variant="subtle"
            color="gray"
            leftSection={<IconX size={14} />}
            onClick={(e) => { e.stopPropagation(); cancelMut.mutate(r) }}
            loading={cancelMut.isPending && cancelMut.variables?.id === r.id}
          >
            Cancelar
          </Button>
        </Group>
      )
    }
    if (r.status === 'executing') {
      return (
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="xs" c="dimmed">Em execução</Text>
        </Group>
      )
    }
    return <Text size="xs" c="dimmed">—</Text>
  }

  // --- Sorting ---
  const toggleSort = (col: SortCol) => {
    setSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: 'desc' },
    )
  }

  const SortIndicator = ({ col }: { col: SortCol }) => {
    if (sort.col !== col) return <IconArrowsSort size={12} style={{ opacity: 0.4 }} />
    return sort.dir === 'asc'
      ? <IconChevronUp size={12} />
      : <IconChevronDown size={12} />
  }

  const SortableTh = ({ col, children }: { col: SortCol; children: React.ReactNode }) => (
    <Table.Th>
      <UnstyledButton
        onClick={() => toggleSort(col)}
        style={{
          display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer',
          // O tema poe th { textTransform: 'uppercase', letterSpacing, fontSize, fontWeight,
          // color } (theme/index.ts:96), mas o reset do Mantine tem
          // `button, select { text-transform: none }` — que sobrepoe a heranca. Sem estas
          // linhas, cabecalho com seta saia em sentence case e cabecalho sem seta em CAIXA
          // ALTA, na mesma linha. Estilo inline vence seletor de folha.
          font: 'inherit', color: 'inherit',
          textTransform: 'inherit', letterSpacing: 'inherit',
        }}
      >
        <span>{children}</span>
        <SortIndicator col={col} />
      </UnstyledButton>
    </Table.Th>
  )

  // --- Filter + sort pipeline ---
  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase()
    const filtered = q
      ? allItems.filter(
          (r) =>
            (r.tenant_name?.toLowerCase().includes(q) ?? false) ||
            String(r.tenant_id).includes(q) ||
            r.account_id.toLowerCase().includes(q),
        )
      : allItems

    return [...filtered].sort((a, b) => {
      let cmp = 0
      switch (sort.col) {
        case 'rec_type':
          cmp = recTypeLabel(a.rec_type).localeCompare(recTypeLabel(b.rec_type), 'pt-BR')
          break
        case 'account_id':
          cmp = a.account_id.localeCompare(b.account_id, 'pt-BR')
          break
        case 'estimated': {
          const ea = asNumber(a.estimated_monthly_savings_usd) ?? 0
          const eb = asNumber(b.estimated_monthly_savings_usd) ?? 0
          cmp = ea - eb
          break
        }
        case 'confidence':
          cmp = LEVEL_ORDER[a.confidence] - LEVEL_ORDER[b.confidence]
          break
        case 'risk':
          cmp = LEVEL_ORDER[a.risk] - LEVEL_ORDER[b.risk]
          break
        case 'status':
          cmp = STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
          break
      }
      return sort.dir === 'asc' ? cmp : -cmp
    })
  }, [allItems, query, sort])

  // --- Grouping by tenant_id ---
  const groups = useMemo(() => {
    const map = new Map<number, { tenantLabel: string; items: FinopsRecommendation[] }>()
    for (const r of filteredItems) {
      if (!map.has(r.tenant_id)) {
        map.set(r.tenant_id, {
          tenantLabel: r.tenant_name ?? `#${r.tenant_id}`,
          items: [],
        })
      }
      map.get(r.tenant_id)!.items.push(r)
    }
    return Array.from(map.entries()).map(([tenantId, { tenantLabel, items }]) => {
      const subtotal = items.reduce((sum, r) => {
        if (!ACTIVE_STATUSES.has(r.status)) return sum
        return sum + (asNumber(r.estimated_monthly_savings_usd) ?? 0)
      }, 0)
      return { tenantId, tenantLabel, items, subtotal }
    })
  }, [filteredItems])

  // Abre o primeiro grupo na primeira chegada de dados. Sem isso, toda linha visivel e
  // cabecalho de grupo com colSpan={COL_COUNT} e os nove cabecalhos prometem dado que nao
  // esta na tela.
  //
  // useRef, e nao `expandedGroups.size === 0`: com guarda de tamanho, recolher o unico
  // grupo aberto zera o tamanho e o efeito reabre — o usuario nao consegue fechar. E
  // `groups` troca de identidade a cada filtro, entao reabriria depois de filtrar tambem.
  const jaAbriuPrimeiro = useRef(false)
  useEffect(() => {
    if (jaAbriuPrimeiro.current || groups.length === 0) return
    jaAbriuPrimeiro.current = true
    setExpandedGroups(new Set([groups[0].tenantId]))
  }, [groups])

  const toggleGroup = (tenantId: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(tenantId)) next.delete(tenantId)
      else next.add(tenantId)
      return next
    })
  }

  const toggleRow = (rowId: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(rowId)) next.delete(rowId)
      else next.add(rowId)
      return next
    })
  }

  return (
    <Stack>
      <Group justify="flex-start" align="flex-end">
        <SegmentedControl
          size="xs"
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as StatusFilter)}
          data={[
            { label: 'Todas', value: 'all' },
            { label: 'Detectadas', value: 'detected' },
            { label: 'Pendentes', value: 'pending' },
            { label: 'Em execução', value: 'executing' },
            { label: 'Encerradas', value: 'closed' },
          ]}
        />
        {!isFilteredView && (
          <Select
            size="xs"
            placeholder="Todos os tipos"
            clearable
            value={recTypeFilter}
            onChange={setRecTypeFilter}
            data={recTypeOptions}
            w={200}
          />
        )}
        <TextInput
          size="xs"
          placeholder="Filtrar por UO ou conta…"
          leftSection={<IconSearch size={14} />}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          w={220}
        />
      </Group>

      {recsQuery.isLoading ? (
        /* Tres esqueletos com a forma da linha, e nao um bloco de 200px: bloco unico faz o
           layout saltar quando o dado chega. Mesmo tratamento da aba de Banco de Dados. */
        <Stack gap="xs">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={36} />
          ))}
        </Stack>
      ) : recsQuery.isError ? (
        /* Sem este ramo, `recsQuery.data ?? []` fazia falha de rede cair no ramo vazio e a
           tela afirmava "Nenhuma recomendacao" — dizer que a conta esta limpa quando o que
           houve foi falha e afirmar o que ninguem observou. Em FinOps, economia potencial
           apresentada como zero e decisao de nao agir sobre dado que nao existe. */
        <Alert
          color="red"
          icon={<IconAlertCircle size={16} />}
          title="Não foi possível carregar as recomendações"
        >
          <Stack gap="xs" align="flex-start">
            <Text size="sm">
              A lista não pôde ser carregada, então esta tela não sabe quantas
              recomendações existem. Não conclua que não há nenhuma.
            </Text>
            <Button size="xs" variant="light" color="red" onClick={() => recsQuery.refetch()}>
              Tentar de novo
            </Button>
          </Stack>
        </Alert>
      ) : filteredItems.length === 0 ? (
        /* Duas situacoes diferentes, duas frases: quem nunca teve recomendacao nao filtrou
           nada, e mandar essa pessoa revisar o filtro e mandar procurar o que nao existe. */
        allItems.length === 0 ? (
          <EmptyState
            bare
            size="sm"
            accent="success"
            icon={IconBulb}
            title="Nenhuma recomendação encontrada"
            description="O motor de análise não identificou economia pendente nas contas visíveis para você."
          />
        ) : (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            Nenhuma recomendação para o filtro selecionado. Limpe os filtros para ver as{' '}
            {allItems.length} recomendações existentes.
          </Text>
        )
      ) : (
        // minWidth 1200, e nao 900: com 900 as nove colunas comprimiam para caber nos 1080px do
        // conteudo, e os badges elipsavam — 'RESERVED INSTANCE' recebia 35px dos 109 que precisa.
        // Medido em 2026-08-13: faltavam ~93px somando os elementos truncados. Tabela larga e
        // legitima quando rola DENTRO do proprio container; o que nao pode e o corpo da pagina
        // rolar na horizontal.
        <Table.ScrollContainer minWidth={1200}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <SortableTh col="rec_type">Tipo</SortableTh>
                <Table.Th>Recurso</Table.Th>
                <SortableTh col="account_id">Conta</SortableTh>
                <SortableTh col="estimated">Economia estimada (US$/mês)</SortableTh>
                <Table.Th>Economia realizada</Table.Th>
                <SortableTh col="confidence">Confiança</SortableTh>
                <SortableTh col="risk">Risco</SortableTh>
                <SortableTh col="status">Status</SortableTh>
                <Table.Th>Ações</Table.Th>
              </Table.Tr>
            </Table.Thead>

            {groups.map(({ tenantId, tenantLabel, items: groupItems, subtotal }) => {
              const isGroupExpanded = expandedGroups.has(tenantId)
              return (
                <Table.Tbody key={tenantId}>
                  {/* Group header row */}
                  <Table.Tr
                    style={{ backgroundColor: 'var(--mantine-color-default-hover)', cursor: 'pointer' }}
                    onClick={() => toggleGroup(tenantId)}
                  >
                    <Table.Td colSpan={COL_COUNT} py={6} px="md">
                      <Group gap="sm" align="center">
                        <ActionIcon
                          size="xs"
                          variant="transparent"
                          component="span"
                          aria-label={isGroupExpanded ? 'Recolher grupo' : 'Expandir grupo'}
                        >
                          {isGroupExpanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                        </ActionIcon>
                        <Text fw={600} size="sm">{tenantLabel}</Text>
                        <Badge size="sm" variant="light" color="gray">{groupItems.length}</Badge>
                        {subtotal > 0 && (
                          <Text size="xs" c="green" fw={500}
                                style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatUSD(subtotal)} em aberto
                          </Text>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>

                  {/* Data rows — only when group is expanded */}
                  {isGroupExpanded &&
                    groupItems.map((r) => {
                      const est = asNumber(r.estimated_monthly_savings_usd)
                      const real = asNumber(r.realized_monthly_savings_usd)
                      const isRowExpanded = expandedRows.has(r.id)
                      return (
                        <React.Fragment key={r.id}>
                          <Table.Tr
                            style={{ cursor: 'pointer' }}
                            onClick={() => toggleRow(r.id)}
                          >
                            <Table.Td>
                              <Group gap={4} wrap="nowrap">
                                <ActionIcon
                                  size="xs"
                                  variant="transparent"
                                  component="span"
                                  aria-label={isRowExpanded ? 'Recolher detalhe' : 'Expandir detalhe'}
                                >
                                  {isRowExpanded
                                    ? <IconChevronDown size={12} />
                                    : <IconChevronRight size={12} />}
                                </ActionIcon>
                                <Badge variant="light" color="gray" style={{ flexShrink: 0 }}>
                                  {recTypeLabel(r.rec_type)}
                                </Badge>
                              </Group>
                            </Table.Td>
                            <Table.Td>
                              <Text size="xs" ff="monospace">{r.resource_id}</Text>
                              {r.region && <Text size="xs" c="dimmed">{r.region}</Text>}
                            </Table.Td>
                            <Table.Td>{r.account_id}</Table.Td>
                            {/* tabular-nums na celula de DADO, nao so no subtotal do
                                grupo: sem largura de digito fixa os numeros nao formam
                                coluna legivel entre linhas. */}
                            <Table.Td style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {est != null ? formatUSD(est) : '—'}
                            </Table.Td>
                            <Table.Td>
                              {real != null ? (
                                <Text size="sm" fw={600} c="green">{formatUSD(real)}</Text>
                              ) : (
                                '—'
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Badge variant="light" color={CONFIDENCE_COLORS[r.confidence]}>
                                {LEVEL_LABELS[r.confidence]}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Badge variant="light" color={RISK_COLORS[r.risk]}>
                                {LEVEL_LABELS[r.risk]}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Badge variant="light" color={STATUS_COLORS[r.status]}>
                                {STATUS_LABELS[r.status]}
                              </Badge>
                              {r.status === 'failed' && r.error_message && (
                                <Text size="xs" c="red" mt={4}>{r.error_message}</Text>
                              )}
                            </Table.Td>
                            <Table.Td onClick={(e) => e.stopPropagation()}>
                              {renderRowActions(r)}
                            </Table.Td>
                          </Table.Tr>

                          {/* Expanded detail row */}
                          {isRowExpanded && (
                            <Table.Tr style={{ backgroundColor: 'var(--mantine-color-default-hover)' }}>
                              <Table.Td colSpan={COL_COUNT} px="xl" py="sm">
                                <Stack gap={4}>
                                  <Text size="sm">{recExplanation(r)}</Text>
                                  <Group gap="lg">
                                    <Text size="xs" c="dimmed">
                                      Recurso: <Text component="span" ff="monospace" size="xs">{r.resource_id}</Text>
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      Região: <Text component="span" size="xs">{r.region ?? '—'}</Text>
                                    </Text>
                                    {r.detection_payload &&
                                      Object.entries(r.detection_payload).length > 0 && (
                                        <Box>
                                          <Text size="xs" c="dimmed">
                                            Payload:{' '}
                                            <Text component="span" ff="monospace" size="xs">
                                              {JSON.stringify(r.detection_payload)}
                                            </Text>
                                          </Text>
                                        </Box>
                                      )}
                                  </Group>
                                </Stack>
                              </Table.Td>
                            </Table.Tr>
                          )}
                        </React.Fragment>
                      )
                    })}
                </Table.Tbody>
              )
            })}
          </Table>
        </Table.ScrollContainer>
      )}

      {!canRecommend && !canExecute && (
        <Text size="xs" c="dimmed" ta="center" mt="sm">
          Você não tem permissão para agir sobre recomendações. Solicite o perfil
          com finops:recommend (recomendar/descartar) ou finops:execute (aprovar/executar).
        </Text>
      )}
    </Stack>
  )
}
